import { useEffect, useState } from "react";
import { CheckCircle2, Home, Inbox, UserPlus, X } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/Badge";
import { Select } from "../../components/ui/Select";
import { api, ApiException } from "../../lib/api";
import { cn } from "../../lib/cn";

interface RequestRow {
  id: string;
  email: string;
  name: string | null;
  message: string | null;
  status: "pending" | "approved" | "declined";
  created_at: string;
  reviewed_at: string | null;
}

type Filter = "pending" | "all";

export function RequestsPanel({ circleId, isPlatformAdmin }: { circleId: string; isPlatformAdmin: boolean }) {
  const [filter, setFilter] = useState<Filter>("pending");
  const [requests, setRequests] = useState<RequestRow[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [roleFor, setRoleFor] = useState<Record<string, "member" | "admin">>({});

  async function refresh(f: Filter = filter) {
    const params = f === "all" ? "?status=all" : "?status=pending";
    const data = await api.get<{ requests: RequestRow[] }>(`/api/access-requests${params}`);
    setRequests(data.requests);
  }

  useEffect(() => {
    refresh().catch((err) => setError(err instanceof ApiException ? err.message : "Failed to load."));
  }, []);

  useEffect(() => {
    refresh(filter).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function approveToCircle(id: string, role: "member" | "admin") {
    setBusyId(id); setError(null); setInfo(null);
    try {
      await api.post(`/api/access-requests/${id}/approve`, { circle_id: circleId, role });
      setInfo("Invite sent.");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Approve failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function approveAsFamilyHead(id: string, name: string | null) {
    if (!confirm(`Approve ${name ?? "this person"} as a new family head? They'll create their own private care circle.`)) return;
    setBusyId(id); setError(null); setInfo(null);
    try {
      await api.post(`/api/access-requests/${id}/approve`, { as_family_head: true });
      setInfo("Welcome email sent. They'll create their own circle on first sign-in.");
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Approve failed.");
    } finally {
      setBusyId(null);
    }
  }

  async function decline(id: string) {
    if (!confirm("Decline this request? They won't be notified.")) return;
    setBusyId(id); setError(null); setInfo(null);
    try {
      await api.post(`/api/access-requests/${id}/decline`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Decline failed.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Access requests</h2>
          <p className="mt-0.5 text-sm text-text-muted">
            People who've asked to join Carol Care. Approve to send them an invite.
          </p>
        </div>
        <div className="inline-flex p-1 bg-surface-2 rounded-md">
          {(["pending", "all"] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "px-3 py-1 rounded text-sm font-medium",
                filter === f ? "bg-surface text-text shadow-sm" : "text-text-muted hover:text-text",
              )}
            >
              {f === "pending" ? "Pending" : "All"}
            </button>
          ))}
        </div>
      </div>

      {info && (
        <div className="mt-3 rounded-md bg-success-soft border border-green-200 text-success text-sm px-3 py-2 flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" /> {info}
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-md bg-danger-soft border border-red-200 text-danger text-sm px-3 py-2">{error}</div>
      )}

      {requests === null ? (
        <div className="mt-6 text-text-muted">Loading…</div>
      ) : requests.length === 0 ? (
        <div className="mt-6 rounded-lg border border-border bg-surface-2 p-6 text-center">
          <Inbox className="h-6 w-6 mx-auto text-text-faint" />
          <p className="mt-2 text-sm text-text-muted">
            {filter === "pending" ? "No pending requests. " : "No requests yet."}
            {filter === "pending" && "Strangers can ask for access via the sign-in page."}
          </p>
        </div>
      ) : (
        <ul className="mt-4 space-y-3">
          {requests.map((r) => {
            const role = roleFor[r.id] ?? "member";
            return (
              <li key={r.id} className="rounded-lg border border-border bg-surface p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{r.name ?? r.email}</div>
                    {r.name && <div className="text-xs text-text-muted truncate">{r.email}</div>}
                    <div className="text-xs text-text-faint mt-0.5">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  {r.status === "pending" ? (
                    <Badge tone="warning">Pending</Badge>
                  ) : r.status === "approved" ? (
                    <Badge tone="success">Approved</Badge>
                  ) : (
                    <Badge tone="neutral">Declined</Badge>
                  )}
                </div>
                {r.message && (
                  <div className="mt-3 rounded-md bg-surface-2 border border-border p-3 text-sm whitespace-pre-line">
                    {r.message}
                  </div>
                )}
                {r.status === "pending" && (
                  <div className="mt-3 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Select
                        value={role}
                        onChange={(e) => setRoleFor({ ...roleFor, [r.id]: e.target.value as "member" | "admin" })}
                        className="w-32 h-9 text-sm"
                      >
                        <option value="member">As member</option>
                        <option value="admin">As admin</option>
                      </Select>
                      <Button
                        onClick={() => approveToCircle(r.id, role)}
                        loading={busyId === r.id}
                        size="md"
                      >
                        <UserPlus className="h-4 w-4" /> Invite to this circle
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => decline(r.id)}
                        loading={busyId === r.id}
                        size="md"
                        className="text-danger"
                      >
                        <X className="h-4 w-4" /> Decline
                      </Button>
                    </div>
                    {isPlatformAdmin && (
                      <div>
                        <Button
                          variant="secondary"
                          onClick={() => approveAsFamilyHead(r.id, r.name)}
                          loading={busyId === r.id}
                          size="md"
                        >
                          <Home className="h-4 w-4" /> Approve as new family head
                        </Button>
                        <p className="mt-1 text-xs text-text-faint">
                          They'll get a welcome email and set up their own private care circle.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
